/**
 * Live document-tag preview. In the default 'section' variant it shows the
 * conditional tag pair a key derives in the Word template ({#key} … {/key}) and
 * copies both tags newline-separated; in the 'plain' variant it shows and
 * copies a single tag ({key}) — used for the checkbox marker, which is a plain
 * placeholder, not a section. Both render the same monospace styling and copy
 * button, with an aria-live success announcement. Renders nothing for an empty
 * tag.
 */

import { useState } from 'react';
import { Copy } from 'lucide-react';

export interface TagPreviewProps {
  /** The derived tag key, e.g. 'hardware_enabled'. */
  tag: string;
  /** Human label used in the copy button's aria-label and the announcement. */
  label?: string;
  /** 'section' (default) copies the {#…}/{/…} pair; 'plain' copies {…} alone. */
  variant?: 'section' | 'plain';
}

export default function TagPreview({ tag, label, variant = 'section' }: TagPreviewProps) {
  const [message, setMessage] = useState('');

  if (tag.trim() === '') return null;

  const display = variant === 'plain' ? `{${tag}}` : `{#${tag}} … {/${tag}}`;
  const clipboard = variant === 'plain' ? `{${tag}}` : `{#${tag}}\n{/${tag}}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clipboard);
      setMessage(label ? `${label} copied` : 'Tag copied');
    } catch {
      setMessage('Copy failed');
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <code className="font-mono text-caption text-slate-500 dark:text-slate-400 break-all">
        {display}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={`Copy ${label ?? `tag ${display}`}`}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:text-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-brand-400"
      >
        <Copy className="h-3 w-3" aria-hidden="true" />
      </button>
      <span data-testid="tag-preview-live" aria-live="polite" className="sr-only">
        {message}
      </span>
    </span>
  );
}
